/**
 * This file contains all the query keys used by react-query,
 * such they can be referenced by name. Each key is an array,
 * and subsction or variable query params are concatenated onto
 * the end of the array when used in a query
 */

export const MESSAGE_LIBRARY = {
  ALL: ['library'],
  TEMPLATES: ['library', 'templates'],
  BROADCASTS: ['library', 'broadcasts'],
  AUTOMATIONS: ['library', 'automations'],
  CLIENT_MESSAGE: ['client', 'message'],
  MESSAGE_STATS: ['library', 'message', 'stats'],
  MESSAGE: ['message'],
  MESSAGE_SYNCED_EMAIL: ['message', 'synced_email'],
};

export const TIMELINE = {
  ALL: ['timeline'],
  // Key used for all timeline fetches of a specific record Id
  RECORD: (recordId) => ['timeline', recordId],
  EVENT_TYPES: ['timeline', 'eventtypes'],
  PROBE_BLOCK: (eventId, created, blockId, filterPayload) => [
    'probe_timeline',
    blockId,
    eventId,
    created,
    filterPayload,
  ],
  TYPES: ['timeline', 'types'],
};

export const ACTIVITIES = {
  ALL: ['activities'],
  DEFAULT_RELATIONSHIPS: ['activities', 'default-relationships'],
  LOGGABLE: (params) => ['activities', 'loggable', params].filter(Boolean),
  ACTIVITY: (id) => ['activities', id],
  ACTIVITY_CAMEL: (id) => ['activities', id, 'camel'],
  ACTIVITY_FIELDS: (id) => ['activities', id, 'fields'],
  CUSTOM_SCHEDULED: (objectId, entityId) =>
    ['activities', 'custom-scheduled', objectId, entityId].filter(Boolean),
  PRIMARY_ASSOCIATIONS: (recordId, activityId) =>
    ['activities', 'primary-associations', recordId, activityId].filter(
      Boolean
    ),
  SCHEDULED_ACTIVITY: (id, criteria) =>
    ['activities-settings', 'scheduled-activity-table', id, criteria].filter(
      Boolean
    ),
  LOGGED_ACTIVITY: (id, criteria) =>
    ['activities-settings', 'logged-activity-table', id, criteria].filter(
      Boolean
    ),
  ACTIVITY_TABLE: (
    dashletId,
    activityIds = [],
    criteria,
    teamMemberFilter,
    roleFilter,
    filter,
    sort,
    includeEmployees,
    includeEmployeesWithRoles,
    includeRoles
  ) =>
    [
      'activity-table',
      dashletId,
      'activities',
      'fetch-activities',
      ...activityIds,
      'criteria',
      criteria,
      'teamMemberFilter',
      teamMemberFilter,
      'roleFilter',
      roleFilter,
      'filter',
      filter,
      'sort',
      sort,
      'includeEmployees',
      includeEmployees,
      'includeEmployeesWithRoles',
      includeEmployeesWithRoles,
      'includeRoles',
      includeRoles,
    ].filter(Boolean),
};

export const FORMS = {
  ALL: ['forms'],
  FORM_RELATED_OBJECT_FIELDS: (id) => ['forms', 'fields', id],
};

export const CUSTOM_OBJECTS = {
  API_NAME_TO_ID: (apiName) => ['custom_objects', 'api_name_to_id', apiName],
  CATEGORIES: (id) => ['custom_objects', 'categories', id],
  CONTACT_CATEGORIES: ['client', 'categories'],
  CONTACT_FIELDS: ['client', 'fields'],
  CONTACT_FIELDS_UNRESTRICTED: ['client', 'fields', 'unrestricted'],
  CONTACT_RECORD: (id, params) => ['client', id, params],
  DETAILS: (id) => ['custom-objects', id, 'details'],
  MODEL: (id) => ['custom_object', 'model', id],
  LIST: ['custom_objects', 'list'],
  FIELDS_UNRESTRICTED: (id) => ['custom_objects', 'fields', id, 'unrestricted'],
  FIELDS_CATEGORIZED: (id) => ['custom_objects', 'fields', id, 'categorized'],
  RECORD_DETAIL_LAYOUT: (objectId) => [
    'custom_objects',
    'record_detail_layout',
    objectId,
  ],
  LIST_LAYOUTS: (objectId) => ['custom_object', 'list_layouts', objectId],
  RECORD: (objectId, entityId, params) => [
    'custom_object',
    objectId,
    'entity_records',
    entityId,
    params,
  ],
  FIELDS: (objectId, fieldId) => [
    'custom_objects',
    objectId,
    'fields',
    fieldId,
  ],
  COLUMN_TEMPLATES: (objectId) => [
    'custom_objects',
    'column_templates',
    objectId,
  ],
  CATEGORIZED_FIELDS: (objectId) => [
    'custom_objects',
    'categorized_fields',
    objectId,
  ],
};

export const CUSTOM_RECORDS = {
  LIST: (id) => ['custom_objects', id, 'records', 'list'],
  DETAIL: (objectId, id) =>
    ['custom_records', 'detail', objectId, id].filter(Boolean),
  ACTIVITY_LIST: (id) => ['custom_records', id, 'activities', 'list'],
  LOGGABLE_ACTIVITY_LIST: (id) => [
    'custom_records',
    id,
    'activities',
    'loggable',
    'list',
  ],
  TYPEAHEAD: (id) => ['custom_objects', 'typeahead', id],
};

export const AUTOMATIONS = {
  AUTOMATION: (id) => ['automations', id],
  TYPEAHEAD: ['automations', 'typeahead'],
  TYPEAHEAD_FILTERED: ['automations', 'typeahead-filtered'],
  META_DATA: ['automations', 'metaData'],
  AUTOMATION_MESSAGE: (id, messageId) => [
    'automations',
    id,
    'messages',
    messageId,
  ],
};

export const LEAD_SOURCES = {
  COMBINED_SEARCH: ['lead_sources', 'combined'],
  TABLE: (id) => ['lead_sources', 'table', id],
  TYPE_AHEAD: (id) => ['lead_sources', id],
};

export const EXTERNAL_ACCOUNT = {
  ALL: ['external-account'],
};

export const EXTERNAL_ACCOUNT_ALL = {
  ALL: ['external-account-all'],
  FILTERED: (fetchArgs) => ['external-account-all', fetchArgs],
  GET: (id) => ['external-account-all', 'get', id],
};

export const BUSINESS = {
  ALL: ['business-config'],
  COUNTRIES: ['business-countries'],
  HOLIDAYS_COUNTRIES: ['business-holidays-countries'],
  ROLES: ['dashboard', 'roles'],
  TEXT_NUMBER: ['business-text-number'],
};

export const TEAM_MEMBERS = {
  ALL: ['team_members'],
  TYPEAHEAD_LIST: (include_service_accounts = false) => [
    'team_members',
    'list',
    'typeahead',
    include_service_accounts,
  ],
  ROLES: ['team_members', 'roles'],
  GET: (id) => ['team_members', 'get', id],
};

export const ROLES = {
  ALL: ['roles'],
};

export const PRIVACY = {
  BLOCKED_EMAILS: ['blocked.emails'],
};

export const IMAGES = {
  ALL: ['images'],
  SORTED: (sort, search) => ['images', sort, search],
};
export const FILES = {
  ALL: ['files'],
};

export const FIELDS = {
  CLIENT: () => ['client', 'all-fields'],
  CUSTOM_OBJECTS: (type, id) => [type, 'all-fields', id],
  GET_FIELD: (id) => ['fields', 'get', id],
  DYNAMIC_TAGS: (fieldId, modelId) => ['dynamic_tags', fieldId, modelId],
  DYNAMIC_TAGS_TYPEAHEAD: (id) => ['dynamic_tags', 'typeahead', id],
  DYNAMIC_TAGS_NAMES: (ids) => ['dynamic_tags', 'names', ids],
  FACETS: (fieldId) => ['facets', fieldId],
  RELATIONSHIPS: (objectId) => ['fields', 'relationships', objectId],
};

export const CATEGORIES = {
  CLIENT: () => ['client', 'all-category'],
  CUSTOM_OBJECTS: (type, id) => [type, 'all-category', id],
};

export const DASHBOARD = {
  DASHBOARDS: ['dashboard', 'list'],
  DASHBOARDS_OTHERS: ['dashboard', 'list', 'others'],
  DASHBOARD: ['dashboard', 'single'],
  DASHBOARD_CONFIG: ['dashboard', 'config', 'page'],
  DASHLET_PIPELINES: ['dashboard', 'dashlet', 'pipelines'],
  DASHLET_PIPELINE: ['dashboard', 'dashlet', 'pipeline'],
  DASHLET_OBJECT_FIELDS: ['dashboard', 'dashlet', 'object', 'fields'],
  DASHLET_DATA: ['dashboard', 'dashlet', 'data'],
  TEAM_MEMBER_LIST: ['dashboard', 'teamMemberList'],
  ACTIVITY_TYPES: ['dashboard', 'activities'],
  DASHLET_ACTIVITY: ['dashboard', 'dashlet', 'activity'],
  PIPELINE_STAGES: ['dashboard', 'dashlet', 'pipeline', 'stages'],
  GRANTING_ADMINS: ['dashboard', 'granting_admins'],
  CUSTOM_OBJECT_LIST: ['dashboard', 'custom_object', 'list'],
  EMAIL_INTERACTION_STATS_LIST: ['dashboard', 'interaction', 'list'],
  DASHLET_SUBROWS: ['dashboard', 'dashlet', 'subrows'],
};

export const BROADCAST = {
  BROADCAST_CONFIG: ['broadcast', 'config', 'page'],
  STATS: ['broadcast', 'stats'],
};

export const CLIENTS = {
  DEFAULT_MODEL: ['client', 'default-model'],
  GROUPS: ['client', 'groups'],
  DETAIL: (id) => ['client', 'detail', id].filter(Boolean),
  ENTITY: (id) => ['client', 'entity', id],
  ADDRESSES: (id) => ['client', 'entity', 'addresses', id],
};

export const PAGE_CONFIGS = {
  /**
   * @typedef {Object} Opts
   * @property {string} opts.business
   * @property {string} opts.user
   * @property {boolean} opts.builder
   *
   * @param {...Opts} opts
   */
  MY_TOOLBAR: (opts) => {
    const base = ['page_config', 'my_toolbar'];

    if (!opts) {
      return base;
    }

    return [...base, opts];
  },
  SCHEDULED_ACTIVITIES: ['page_config', 'scheduled_activities'],
  ACTIVITY_TABLE_CONFIG: (id) => [
    'page_config',
    'scheduled_logged_activity_table_config',
    id,
  ],
  BILLING_NOTICE: ['page_config', 'billing_notice'],
};

export const TOOLBAR_TEMPLATES = {
  LIST: ['toolbar_templates', 'list'],
  SINGLE: ['toolbar_templates', 'single'],
  GRANTING_ADMINS: ['toolbar_templates', 'granting_admins'],
};

export const RECENT_ITEMS = {
  LIST: (objects = [], count) => ['recent_items', 'list', ...objects, count],
};

export const DETAILS_RELATED_OBJECTS = {
  ALL: ['details_related_objects'],
  RECORD: (modelId, entityId) => ['details_related_objects', modelId, entityId],
};

const RELATED_PIPELINES_COMMON = ['related_pipelines', 'common'];

export const DETAILS_RELATED_PIPELINES = {
  LIST: (entityId, pageSize, queryBody, searchTerm) => [
    ...RELATED_PIPELINES_COMMON,
    entityId,
    pageSize,
    queryBody,
    searchTerm,
  ],
  REFRESH: (entityId) => [...RELATED_PIPELINES_COMMON, entityId],
};

export const RELATION_FIELD = {
  RELATED_OBJECTS: (id) => ['related_objects', id],
};

export const ACCOUNT = {
  ALL: ['account'],
  API_KEYS: ['account', 'api_keys'],
  OAUTH_TOKENS: ['account', 'oauth_tokens'],
};

export const RELATIONSHIP_KEY = (value) => [`relationship`, value];

/**
 * type LoadFilterOptions = {
 *  method: string;
 *  url: string;
 *  body: any;
 *  params: any;
 *  result_path?: string[];
 *  result_transform?: any;
 * }
 */
export const FILTERS = {
  QUERY: (opts) => ['meta-filters', opts],
  INFINITE_QUERY: (opts) => ['meta-filters', opts, { infinite: true }],
};

export const OBJECT_FIELDS = {
  OBJECT: (type) => ['object_fields', type],
};

export const RECORDS = {
  DISPLAY_NAMES: (payload) => ['records', 'display-names', payload],
};

export const OBJECT_GROUPS = {
  OBJECT: (id) => ['object_groups', id],
};

export const PLUGINS = {
  LIST: ['plugins', 'list'],
  CONFIG: ['plugins', 'config', 'page'],
  GET_PLUGIN_APPS: (search, installed, page, page_size) => [
    'plugins',
    'get_plugin_apps',
    search,
    page,
    page_size,
    installed,
  ],
  GET_PLUGIN_APP: (api_name) => ['plugins', 'get_plugin_app', api_name],
  GET_MOCKED_PLUGIN_APPS: ['plugins', 'get_mocked_plugin_apps'],
  GET_BUSINESS_PLUGIN_APPS: ['plugins', 'get_plugin_apps', 'business'],
  GET_RELEASE_NOTES: (api_name) => ['plugins', 'release_notes', api_name],
  GET_EVENTS: (id) => ['plugins', 'events', id],
  IMPORT_PROGRESS: (id) => ['plugins', 'import_progress', id],
  GET_AUTHORIZED_USERS: (id, serviceId) => [
    'plugins',
    'authorized_users',
    id,
    serviceId,
  ],
  GET_JS_ACTION_TEMPLATE_ASSOCIATIONS: (businessPluginAppId) => [
    'plugins',
    'js_action_template_associations',
    businessPluginAppId,
  ],
  GET_USER_CONFIG: (api_name) => ['plugins', 'user_config', api_name],
  ENABLED_KEYS: (...attrs) => ['plugins', 'enabled_keys', ...attrs],
  BUSINESS_APPS: ['plugins', 'business_apps'],
  GET_BUSINESS_APP_ASSETS: (id) => ['plugins', 'business_apps', 'assets', id],
  GET_BUSINESS_APPS: (search) => ['plugins', 'business_apps', 'list', search],
  GET_BUSINESS_APP: (apiName, previewOwnerId) => [
    'plugins',
    'business_apps',
    'detail',
    apiName,
    previewOwnerId,
  ],
  GET_BUSINESS_APP_PREVIEW_INSTALLS: (apiName, previewOwnerId) => [
    'plugins',
    'business_apps',
    'installs',
    apiName,
    previewOwnerId,
  ],
  GET_BUSINESS_PLUGIN_APP: (apiName) => [
    'plugins',
    'business_plugin_app',
    apiName,
  ],
};

export const KICODE = {
  LINKED_SESSIONS: (resourcePath, builder, createdBy) => [
    'kicode',
    'linked_sessions',
    resourcePath,
    builder,
    createdBy,
  ],
};

const SMART_CONNECTORS_BASE_KEY = 'smart_connectors';

export const SMART_CONNECTORS = {
  BASE: [SMART_CONNECTORS_BASE_KEY],
  ALL: (pageNumber, pageSize, search, sort) => [
    SMART_CONNECTORS_BASE_KEY,
    pageNumber,
    pageSize,
    search,
    sort,
  ],
};

export const CHAT_BOT = {
  NEW_CHAT_SESSION: (taskType, context) => [
    'chat-bot',
    'new-session',
    taskType,
    context,
  ],
  CHAT_SESSION: (id) => ['chat-bot', id],
  METADATA: ['chat-bot', 'metadata'],
  FLOW_STATUS: (flowType, chatSessionId, entityId) =>
    ['chat-bot', 'flow-status', flowType, chatSessionId, entityId].filter(
      Boolean
    ),
  PUBLIC_BOOTSTRAP: ['chat-bot', 'public-bootstrap'],
  SESSION_HISTORIES: (taskType) => ['chat-bot', 'session-histories', taskType],
  SESSION_HISTORIES_INFINITE: (taskType) => [
    'chat-bot',
    'session-histories',
    taskType,
    'infinite',
  ],
};

export const AI_CHAT = {
  SESSIONS: ['ai-chat', 'sessions'],
  SESSION: (id) => ['ai-chat', 'session', id],
  MESSAGES: (sessionId) => ['ai-chat', 'messages', sessionId],
};

export const QUICK_FILTERS = {
  TEMPLATES: (objectId) => ['quick_filters', 'templates', objectId],
};

export const Billing = {
  PAYMENT_CONTACT: ['billing', 'settings', 'payment-contact'],
  INVOICE: (sortOder) => ['billing', 'invoice', sortOder],
};
